import { useNavigate } from 'react-router-dom';
import { Button, ButtonToolbar, Progress, Stack } from 'rsuite';

import { TbProgress } from 'react-icons/tb';
import { TbProgressX } from 'react-icons/tb';

import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  globalApi,
  useAppDispatch,
  useAppSelector,
  useGetStatusQuery
} from '@monorepo/shared';
import { selectRegions } from '@monorepo/shared/store/authSlice';
import CheckOutlineIcon from '@rsuite/icons/CheckOutline';
import { selectCurrentAccount } from '@monorepo/shared/store/accountSlice';

interface TaskObj {
  sn: string;
  task_id: string;
  account_id: string;
  region_alias: string;
}
const CheckBtn = (data: Record<string, any>) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<string>('active');
  useEffect(() => {
    if (data.data?.status === 'FAILURE') {
      setStatus('fail');
    }
    if (data.data?.status === 'PENDING') {
      setStatus('active');
    }
    if (data.data?.status === 'STARTED') {
      setStatus('active');
    }
    if (data.data?.stage === 'DONE') {
      if (data.arg?.setIsCheck) {
        data.arg.setIsCheck(false);
      }
      setStatus('done');
    }
  }, [data]);
  const goToProvisionProgress = () => {
    navigate(`provision/${data.data.arg?.sn}/${data.data.arg?.region_alias}`);
  };
  return status === 'done' ? (
    <CheckOutlineIcon color="green" />
  ) : (
    <Button
      style={{ width: '100px' }}
      size="xs"
      appearance="ghost"
      color={status === 'fail' ? 'red' : undefined}
      title={`Provision ${data?.data?.status}`}
      onClick={goToProvisionProgress}
      startIcon={
        status === 'active' ? (
          <TbProgress color="var(--fc-blue)" className="icon-spinner" />
        ) : (
          <TbProgressX color="red" />
        )
      }
    >
      Provision...
    </Button>
  );
};

const ProgressDetail = (data: Record<string, any>) => {
  const navigate = useNavigate();
  const [percent, setPercent] = useState<number>(10);
  const [status, setStatus] = useState<any>('active');
  const dispatch = useAppDispatch();
  const currentAccount = useAppSelector(selectCurrentAccount);
  useEffect(() => {
    if (data.data?.status === 'FAILURE' || data.data?.status === 'BLOCKED') {
      setStatus('fail');
    }
    if (data.data?.status === 'STARTED') {
      setStatus('active');
      setPercent(10);
    }
    if (data.data?.status === 'PENDING') {
      setStatus('active');
      setPercent(30);
    }
    if (data.data?.stage === 'LICENSE_CHECK') {
      setPercent(40);
    }
    if (data.data?.stage === 'ASSIGNING') {
      setPercent(50);
    }
    if (data.data?.stage === 'NETWORK_SETUP') {
      setPercent(60);
    }
    if (data.data?.stage === 'LICENSE_INSTALLING') {
      setPercent(70);
    }
    if (data.data?.stage === 'DONE') {
      setStatus('success');
      setPercent(99);
      setTimeout(() => {
        dispatch(globalApi.util.invalidateTags(['Accounts', 'Entitlements']));
        if (currentAccount?.account_id) {
          console.info(
            'provision success, redirect to current account id: ' +
              currentAccount?.account_id
          );
          navigate(`/organization/${currentAccount.account_id}`);
        } else {
          navigate(-1);
        }
      }, 1000);
    }
  }, [data]);

  const statusTextMapping: Record<string, string> = {
    STARTED: 'In Progress',
    PENDING: 'Initializing',
    SUCCESS: 'Completed',
    BLOCKED: 'Awaiting for Admin Approval'
  };

  const stageTextMapping: Record<string, string> = {
    LICENSE_CHECK: 'Validating License',
    ASSIGNING: 'Assigning Instance',
    NETWORK_SETUP: 'Setting Up Network',
    LICENSE_INSTALLING: 'Installing License'
  };

  return (
    <div>
      <div>
        <div style={{ paddingLeft: '12px' }}>
          Provision Instance : {data?.data?.arg?.sn}{' '}
        </div>
        <Progress.Line percent={percent} status={status} showInfo={false} />
        <ul>
          <li>
            <Stack spacing="16px">
              <span>Status:</span>
              <span>
                {statusTextMapping[data?.data?.status] || data?.data?.status}
              </span>
            </Stack>
          </li>
          {data?.data?.status !== 'BLOCKED' ? (
            <li>
              <Stack spacing="16px">
                <span>Stage:</span>
                <span>
                  {stageTextMapping[data?.data?.stage] || data?.data?.state}
                </span>
              </Stack>
            </li>
          ) : (
            <></>
          )}
          {data?.data?.error_code ? (
            <li>
              <Stack spacing="16px">
                <span>Error Code:</span>
                <span>{data?.data.error_code}</span>
              </Stack>
            </li>
          ) : (
            <></>
          )}
          {data?.data?.error_code ? (
            <li>
              <Stack spacing="16px">
                <span>Error Message:</span>
                <span>{data?.data.error_desc}</span>
              </Stack>
            </li>
          ) : (
            <></>
          )}
          {data?.data?.msg ? (
            <li>
              <Stack spacing="16px">
                <span>Message:</span>
                <span>{data?.data.msg}</span>
              </Stack>
            </li>
          ) : (
            <></>
          )}
        </ul>
      </div>
      <ButtonToolbar style={{ margin: '130px', justifyContent: 'center' }}>
        <Button
          appearance="ghost"
          className="action-btn"
          onClick={() => {
            dispatch(
              globalApi.util.invalidateTags(['Accounts', 'Entitlements'])
            );
            navigate(-1);
          }}
        >
          Close
        </Button>
      </ButtonToolbar>
    </div>
  );
};

const ProvisionStatus =
  (Component: FC<any>) =>
  ({ sn, task_id, account_id, region_alias }: TaskObj) => {
    const { t } = useTranslation();
    const [region, setRegion] = useState<Record<string, any>>({});
    const [isCheck, setIsCheck] = useState<boolean>(!!(task_id || sn));
    const [data, setData] = useState<Record<string, any>>({});
    const regions = useAppSelector(selectRegions);
    const { data: provisionStatus, error: provisionError } = useGetStatusQuery(
      {
        region: region,
        license_sn: sn,
        task_id: task_id,
        account_id: account_id || ''
      },
      {
        skip: !isCheck || !region?.token,
        pollingInterval: 10000, // 10 seconds
        refetchOnMountOrArgChange: true
      }
    );

    useEffect(() => {
      if (
        Object.keys(regions).length &&
        region_alias &&
        regions[region_alias]
      ) {
        setRegion(regions[region_alias]);
      }
    }, [regions, region_alias]);
    useEffect(() => {
      if (provisionStatus) {
        const status = provisionStatus?.status || 'Unknown';
        const stage = provisionStatus?.stage || '';
        const msg = provisionStatus?.msg || '';
        const error_code = provisionStatus?.error_code || '';
        const error_desc = provisionStatus?.error_desc || '';
        if (status === 'FAILURE' || status === 'DONE' || status === 'BLOCKED') {
          setIsCheck(false);
        }
        setData({
          status: status,
          stage: stage,
          msg: msg,
          error_code: error_code,
          error_desc: error_desc
        });
      }
      if (provisionError) {
        setData({
          status: 'FAILURE',
          error_desc: 'Get Provision Status Error'
        });
        setIsCheck(false);
      }
    }, [provisionStatus, t, provisionError]);
    return (
      <Component
        data={{ ...data, arg: { task_id, region_alias, sn, setIsCheck } }}
      />
    );
  };
export const ProvisionStatusButton = ProvisionStatus(CheckBtn);
export const ProvisionDetail = ProvisionStatus(ProgressDetail);
